/**
 * EbookTeaserCard — compact waitlist tile shown on the calculator results
 * panel after a successful projection. Purpose: convert warm calculator
 * users into ebook waitlist signups. Silent if the user is already on the
 * waitlist (via a localStorage hint).
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, CheckCircle2, Loader2 } from "lucide-react";
import { joinEbookWaitlist } from "@/lib/api";
import { track, EVENTS } from "@/lib/analytics";
import { errorMessage } from "@/lib/errorMessage";
import { secureGet, secureSet } from "@/lib/secureStorage";
import { toast } from "sonner";

const JOINED_KEY = "centennial_ebook_waitlist_joined_v1";

export default function EbookTeaserCard() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(() => !!secureGet(JOINED_KEY));

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await joinEbookWaitlist(email.trim(), "calculator_results");
      secureSet(JOINED_KEY, "1");
      setJoined(true);
      track(EVENTS.EBOOK_WAITLIST_JOIN, { source: "calculator_results" });
      toast.success("You're on the list.");
    } catch (err) {
      toast.error(errorMessage(err, "Could not join the waitlist."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="ebook-teaser-card"
      className="bg-gradient-to-br from-[#E8F0EB] to-[#F0EFEA] border border-[#2B5339] rounded-2xl p-5 sm:p-6"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] mb-1.5 inline-flex items-center gap-1.5">
            <BookOpen size={11} /> Coming soon · our ebook
          </div>
          <div className="text-lg sm:text-xl font-medium text-[#1C2B23] leading-snug">
            The 100-Year Retirement Plan
          </div>
          <p className="text-xs text-[#5C6B62] mt-1.5 leading-relaxed">
            The playbook behind this calculator — in book form. Waitlist gets a launch-week discount.
          </p>
        </div>
        <Link
          to="/ebook/100-year-retirement-plan"
          data-testid="ebook-teaser-details"
          className="text-xs text-[#2B5339] hover:underline inline-flex items-center gap-1 whitespace-nowrap"
        >
          Details <ArrowRight size={11} />
        </Link>
      </div>

      {joined ? (
        <div
          data-testid="ebook-teaser-success"
          className="mt-4 text-sm text-[#1C2B23] inline-flex items-center gap-2"
        >
          <CheckCircle2 size={15} className="text-[#2B5339]" />
          You&apos;re on the waitlist — we&apos;ll email you the moment it ships.
        </div>
      ) : (
        <form onSubmit={submit} className="mt-4 flex gap-2 flex-wrap sm:flex-nowrap">
          <input
            data-testid="ebook-teaser-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="flex-1 min-w-[180px] bg-white border border-[#E5E2DC] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2B5339]"
          />
          <button
            data-testid="ebook-teaser-submit"
            type="submit"
            disabled={submitting || !email}
            className="bg-[#1C2B23] text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-[#2B5339] transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2 whitespace-nowrap"
          >
            {submitting && <Loader2 size={13} className="animate-spin" />}
            Join waitlist
          </button>
        </form>
      )}
    </div>
  );
}
